import { createFileRoute, useNavigate, Link, Outlet, useLocation } from '@tanstack/react-router';
import { deleteProject, ApiError } from '../../lib/apiClient';
import {
  StatusBadge,
  EmptyState,
  PermissionGate,
  DeleteConfirmationDialog,
} from '../../components/shared';
import { usePermission } from '../../hooks/usePermission';
import { useProjectDetail } from '../../hooks/useProjectDetail';
import type { ProjectMilestone, ProjectOutcome } from '../../graphql/types';
import {
  ProjectKpiCards,
  MilestoneTimeline,
  OutcomeDistributionChart,
  ProgressBurnChart,
  AssignServiceDialog,
  AssignedServicesTable,
  ExportServicesDialog,
  EditMilestoneDialog,
  EditOutcomeDialog,
  MilestonesSection,
  OutcomeCatalogSection,
} from '../../components/projects';
import { useState } from 'react';
import '@tylertech/forge/icon';
import '@tylertech/forge/button';
import '@tylertech/forge/banner';
import './$projectId.css';

export const Route = createFileRoute('/projects/$projectId')({
  component: ProjectDetailPage,
});

export { ProjectDetailPage };

const ForgeButton = 'forge-button' as React.ElementType;
const IconElement = 'forge-icon' as React.ElementType;
const BannerElement = 'forge-banner' as React.ElementType;

function ProjectDetailPage(): JSX.Element {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const canManageProject = usePermission('project:update');

  const {
    project,
    projectLoading,
    projectError,
    milestones,
    outcomes,
    allAssignments,
    kpis,
    distribution,
    burnCounts,
    burnTeamCounts,
    outcomeCountsByMilestoneId,
    serviceResolution,
    teamOptions,
    tagCategories,
    allTags,
    filteredAssignments,
    handleAssignedServicesFiltersChange,
    milestoneInUse,
    outcomeInUse,
    refetchMilestones,
    refetchOutcomes,
    refetchAssignments,
    catalogDeleteError,
    isDeletingCatalog,
    requestDeleteMilestone,
    requestDeleteOutcome,
    deletingMilestone,
    deletingOutcome,
    cancelDeleteMilestone,
    cancelDeleteOutcome,
    confirmDeleteMilestone,
    confirmDeleteOutcome,
  } = useProjectDetail(projectId);

  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editingMilestone, setEditingMilestone] = useState<ProjectMilestone | null>(null);
  const [editingOutcome, setEditingOutcome] = useState<ProjectOutcome | null>(null);

  const handleDeleteProject = async (): Promise<void> => {
    if (!window.confirm(`Delete project "${project?.name}"? This cannot be undone.`)) return;
    setDeleteError(null);
    try {
      await deleteProject(projectId);
      navigate({ to: '/projects', search: { page: 1, pageSize: 25 } });
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : 'Failed to delete project');
    }
  };

  // Edit is a child route; render only the outlet. Must stay after every hook (#1539).
  if (location.pathname.endsWith('/edit')) {
    return <Outlet />;
  }

  if (projectLoading) {
    return (
      <div className="project-detail-page">
        <p className="forge-typography--body1">Loading...</p>
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="project-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load project"
          description={projectError?.message ?? 'Project not found'}
        />
      </div>
    );
  }

  return (
    <div className="project-detail-page">
      <div className="breadcrumb">
        <Link to="/projects" search={{ page: 1, pageSize: 25 }} className="breadcrumb-link">
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to Projects</span>
        </Link>
      </div>

      <div className="page-header page-header--spaced">
        <div className="page-header-title">
          <h1 className="forge-typography--headline4">{project.name}</h1>
          <StatusBadge value={project.status} variant="lifecycle" />
        </div>
        <PermissionGate permission="project:update">
          <div className="page-header-actions">
            <ForgeButton
              type="button"
              variant="outlined"
              onClick={() => navigate({ to: '/projects/$projectId/edit', params: { projectId } })}
            >
              Edit
            </ForgeButton>
            <ForgeButton
              type="button"
              variant="outlined"
              className="action-btn--danger"
              onClick={() => void handleDeleteProject()}
            >
              Delete
            </ForgeButton>
          </div>
        </PermissionGate>
      </div>

      {deleteError && (
        <BannerElement theme="error" dismissed={false} role="alert">
          <span>{deleteError}</span>
        </BannerElement>
      )}

      {catalogDeleteError && (
        <BannerElement theme="error" dismissed={false} role="alert">
          <span>{catalogDeleteError}</span>
        </BannerElement>
      )}

      <div className="project-meta-grid">
        <div className="project-meta-item">
          <span className="forge-typography--caption project-meta-label">Division</span>
          <span className="forge-typography--body2">{project.ownerDivision}</span>
        </div>
        {project.startDate && (
          <div className="project-meta-item">
            <span className="forge-typography--caption project-meta-label">Start Date</span>
            <span className="forge-typography--body2">{project.startDate}</span>
          </div>
        )}
        {project.targetEndDate && (
          <div className="project-meta-item">
            <span className="forge-typography--caption project-meta-label">Target End</span>
            <span className="forge-typography--body2">{project.targetEndDate}</span>
          </div>
        )}
      </div>

      {project.description && <p className="forge-typography--body1">{project.description}</p>}

      {/* Dashboard section */}
      <section
        className="project-detail-section"
        aria-labelledby="dashboard-heading"
        data-testid="project-dashboard"
      >
        <h2 id="dashboard-heading" className="forge-typography--headline6">
          Dashboard
        </h2>
        <ProjectKpiCards kpis={kpis} />
        <MilestoneTimeline
          milestones={milestones}
          projectStartDate={project.startDate ?? undefined}
          projectEndDate={project.targetEndDate ?? undefined}
        />
        <div className="project-dashboard-charts">
          <OutcomeDistributionChart distribution={distribution} />
          <ProgressBurnChart
            statusCounts={burnCounts}
            milestones={milestones}
            teamCounts={burnTeamCounts}
          />
        </div>
      </section>

      {/* Outcome Catalog section — collapsible, moved above Assigned Services (#498) */}
      <OutcomeCatalogSection
        projectId={projectId}
        outcomes={outcomes}
        outcomeInUse={outcomeInUse}
        canManageProject={canManageProject}
        onOutcomeAdded={() => void refetchOutcomes()}
        onEditOutcome={setEditingOutcome}
        onRequestDeleteOutcome={requestDeleteOutcome}
      />

      {/* Milestones section — collapsible, moved above Assigned Services (#498) */}
      <MilestonesSection
        projectId={projectId}
        milestones={milestones}
        outcomeCountsByMilestoneId={outcomeCountsByMilestoneId}
        milestoneInUse={milestoneInUse}
        canManageProject={canManageProject}
        onMilestoneAdded={() => void refetchMilestones()}
        onEditMilestone={setEditingMilestone}
        onRequestDeleteMilestone={requestDeleteMilestone}
      />

      {/* Assigned Services section */}
      <section
        className="project-detail-section"
        aria-labelledby="assigned-services-heading"
        data-testid="assigned-services-section"
      >
        <div className="project-detail-section-header">
          <h2 id="assigned-services-heading" className="forge-typography--headline6">
            Assigned Services
          </h2>
          <div className="project-detail-section-header-actions">
            <ForgeButton type="button" variant="outlined" onClick={() => setShowExportDialog(true)}>
              Export
            </ForgeButton>
            <PermissionGate permission="service:update">
              <ForgeButton
                type="button"
                variant="outlined"
                onClick={() => setShowAssignDialog(true)}
              >
                Assign Service
              </ForgeButton>
            </PermissionGate>
          </div>
        </div>
        <AssignedServicesTable
          assignments={allAssignments}
          milestones={milestones}
          outcomes={outcomes}
          serviceResolution={serviceResolution}
          teamOptions={teamOptions}
          onAssignmentsChanged={() => void refetchAssignments()}
          onFiltersChange={handleAssignedServicesFiltersChange}
        />
        <AssignServiceDialog
          open={showAssignDialog}
          milestones={milestones}
          outcomes={outcomes}
          onSuccess={() => {
            setShowAssignDialog(false);
            refetchAssignments();
          }}
          onCancel={() => setShowAssignDialog(false)}
        />
        <ExportServicesDialog
          open={showExportDialog}
          projectName={project.name}
          assignments={filteredAssignments}
          milestones={milestones}
          serviceResolution={serviceResolution}
          tags={allTags}
          tagCategories={tagCategories}
          onClose={() => setShowExportDialog(false)}
        />
      </section>

      <EditMilestoneDialog
        milestone={editingMilestone}
        onSuccess={() => {
          setEditingMilestone(null);
          void refetchMilestones();
        }}
        onCancel={() => setEditingMilestone(null)}
      />

      <EditOutcomeDialog
        outcome={editingOutcome}
        onSuccess={() => {
          setEditingOutcome(null);
          void refetchOutcomes();
        }}
        onCancel={() => setEditingOutcome(null)}
      />

      {deletingMilestone !== null && (
        <DeleteConfirmationDialog
          open
          title="Delete Milestone"
          message="Are you sure you want to delete this milestone? This cannot be undone."
          itemName={deletingMilestone.name}
          onConfirm={() => void confirmDeleteMilestone()}
          onCancel={cancelDeleteMilestone}
          isDeleting={isDeletingCatalog}
        />
      )}

      {deletingOutcome !== null && (
        <DeleteConfirmationDialog
          open
          title="Delete Outcome"
          message="Are you sure you want to delete this outcome? This cannot be undone."
          itemName={deletingOutcome.name}
          onConfirm={() => void confirmDeleteOutcome()}
          onCancel={cancelDeleteOutcome}
          isDeleting={isDeletingCatalog}
        />
      )}
    </div>
  );
}
